import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Card, ErrorNotice, Field, PageHeader, Spinner } from '@/components/ui';
import { AccountSecurity } from '@/features/account/AccountSecurity';
import { authConfigured } from '@/features/auth/config';
import { CalendarLeaveSettings } from '@/features/calendar/CalendarLeaveSettings';
import { CourseVisibilitySetting } from '@/features/documents/CourseVisibilitySetting';
import { UsersAccess } from '@/features/access/UsersAccess';
import { usePermissions } from '@/features/access/usePermissions';
import { NotificationSettings } from '@/features/notifications/NotificationSettings';
import { useI18n, type Locale, type TranslationKey } from '@/i18n';
import { api, ApiError } from '@/lib/api';
import { useTheme, type ResolvedTheme } from '@/lib/theme';

/*
 * Two states each, matching the sidebar toggles and the public site.
 *
 * Theme has no "system" option here either: the page opens on whatever the OS says and
 * picking one pins it. A third button for "follow the system" is a control almost
 * nobody presses, sitting next to two that explain themselves.
 */
const THEMES: { value: ResolvedTheme; labelKey: TranslationKey }[] = [
  { value: 'light', labelKey: 'theme.light' },
  { value: 'dark', labelKey: 'theme.dark' },
];

const LOCALES: { value: Locale; labelKey: TranslationKey }[] = [
  { value: 'it', labelKey: 'locale.it' },
  { value: 'en', labelKey: 'locale.en' },
  { value: 'ar', labelKey: 'locale.ar' },
];

/** A segmented control: joined buttons, the selected one filled. */
function segment(active: boolean, first: boolean, last: boolean): string {
  return [
    'h-9 px-4 inline-flex items-center justify-center border text-sm font-medium transition-colors',
    first ? 'rounded-s' : '-ms-px',
    last ? 'rounded-e' : '',
    active
      ? 'bg-[var(--color-primary-soft)] text-primary border-[var(--color-border-strong)] font-semibold relative z-10'
      : 'bg-surface text-muted border-border hover:text-text hover:bg-surface-alt',
  ]
    .filter(Boolean)
    .join(' ');
}

const FIELDS = [
  {
    key: 'default_geofence_radius_m',
    prop: 'defaultGeofenceRadiusM',
    labelKey: 'settings.radiusLabel',
    hintKey: 'settings.radiusHint',
  },
  {
    key: 'accuracy_trust_threshold_m',
    prop: 'accuracyTrustThresholdM',
    labelKey: 'settings.accuracyTrustLabel',
    hintKey: 'settings.accuracyTrustHint',
  },
  {
    key: 'accuracy_reject_threshold_m',
    prop: 'accuracyRejectThresholdM',
    labelKey: 'settings.accuracyRejectLabel',
    hintKey: 'settings.accuracyRejectHint',
  },
  {
    key: 'max_shift_hours',
    prop: 'maxShiftHours',
    labelKey: 'settings.maxShiftLabel',
    hintKey: 'settings.maxShiftHint',
  },
  {
    key: 'max_offline_queue_age_hours',
    prop: 'maxOfflineQueueAgeHours',
    labelKey: 'settings.offlineAgeLabel',
    hintKey: 'settings.offlineAgeHint',
  },
  {
    key: 'working_hours_grace_minutes',
    prop: 'workingHoursGraceMinutes',
    labelKey: 'settings.graceLabel',
    hintKey: 'settings.graceHint',
  },
] as const;

/*
 * The three switches, as data.
 *
 * They behave identically and only the wording differs, so a list beats three copies
 * of the same twelve lines of markup — and adding the fourth one is then a line.
 */
const TOGGLES = [
  {
    key: 'allow_clock_in_outside_geofence',
    labelKey: 'settings.allowOutside',
    hintKey: 'settings.allowOutsideHint',
  },
  {
    key: 'allow_clock_in_before_start',
    labelKey: 'settings.allowEarly',
    hintKey: 'settings.allowEarlyHint',
  },
  {
    key: 'allow_clock_out_after_end',
    labelKey: 'settings.allowLate',
    hintKey: 'settings.allowLateHint',
  },
] as const;

/**
 * Settings, in three tabs, each shown only to someone who can use it.
 *
 *   Account          everyone — appearance, and how you sign in. Yours alone, so no
 *                    permission applies: nobody should need an administrator to add a
 *                    passkey to their own account.
 *   Clock-in rules   `settings` — the company-wide GPS and working-hours rules.
 *   Users            `users` — who may open the portal and what they may do in it.
 *
 * This used to be a single page that loaded the company settings before rendering
 * anything, so anyone without that permission opened Settings to an error.
 */
type Tab = 'account' | 'notifications' | 'rules' | 'users';

export function SettingsPage() {
  const { t } = useI18n();
  const { can } = usePermissions();

  const tabs: { key: Tab; label: TranslationKey }[] = [
    { key: 'account', label: 'account.tab' },
    { key: 'notifications', label: 'notifications.tab' },
    ...(can('settings', 'read') ? [{ key: 'rules' as const, label: 'account.tabRules' as const }] : []),
    ...(can('users', 'read') ? [{ key: 'users' as const, label: 'access.tabUsers' as const }] : []),
  ];

  // ?tab= so the bell's "Notification settings" link lands on the right tab.
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab | null) ?? 'account';
  const setTab = (next: Tab) => setParams(next === 'account' ? {} : { tab: next }, { replace: true });
  const active = tabs.some((x) => x.key === tab) ? tab : 'account';

  return (
    <>
      <PageHeader title={t('settings.title')} subtitle={t('settings.subtitle')} />

      {/* No strip at all when there is only one tab: a lone tab reads as a control with
          nothing to control. */}
      {tabs.length > 1 ? (
        <div className="mb-5 flex max-w-full overflow-x-auto" role="tablist">
          {tabs.map((x, i) => (
            <button
              key={x.key}
              type="button"
              role="tab"
              aria-selected={active === x.key}
              onClick={() => setTab(x.key)}
              className={`${segment(active === x.key, i === 0, i === tabs.length - 1)} whitespace-nowrap`}
            >
              {t(x.label)}
            </button>
          ))}
        </div>
      ) : null}

      {active === 'account' ? (
        <div className="max-w-3xl space-y-6">
          <Appearance />
          {/* Passkeys, authenticator and password all live in Cognito. The local
              development sign-in has none, so the card would only show errors there. */}
          {authConfigured ? <AccountSecurity /> : null}
        </div>
      ) : null}
      {active === 'notifications' ? <NotificationSettings /> : null}
      {active === 'rules' ? (
        <div className="max-w-3xl space-y-6">
          <ClockInRules />
          <CalendarLeaveSettings />
          <CourseVisibilitySetting />
          <CompanyDetails />
        </div>
      ) : null}
      {active === 'users' ? <UsersAccess /> : null}
    </>
  );
}

function Appearance() {
  const { t, locale, setLocale } = useI18n();
  const { resolved, setPreference } = useTheme();

  return (
  <Card>
    <h2 className="font-bold mb-1">{t('settings.appearance')}</h2>
    <p className="text-xs text-muted mb-4">{t('settings.appearanceHint')}</p>

    {/*
      Named rows with real controls, not the sidebar's bare circles.
      Those circles work in the sidebar because they are tucked away and carry a
      tooltip; dropped into a settings page with nothing beside them they read as
      three unexplained buttons, and one of them was a link back to this page.
    */}
    <div className="divide-y divide-border border-y border-border">
      <div className="flex items-center justify-between gap-4 py-3">
        <span className="text-sm font-medium">{t('settings.theme')}</span>
        <div className="flex" role="group" aria-label={t('settings.theme')}>
          {THEMES.map((option, i) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={resolved === option.value}
              onClick={() => setPreference(option.value)}
              className={segment(resolved === option.value, i === 0, i === THEMES.length - 1)}
            >
              {t(option.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 py-3">
        <span className="text-sm font-medium">{t('settings.language')}</span>
        <div className="flex" role="group" aria-label={t('settings.language')}>
          {LOCALES.map((option, i) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={locale === option.value}
              onClick={() => setLocale(option.value)}
              className={segment(
                locale === option.value,
                i === 0,
                i === LOCALES.length - 1,
              )}
            >
              {t(option.labelKey)}
            </button>
          ))}
        </div>
      </div>
    </div>
  </Card>
  );
}

/** The company's own details, as printed on every employee's identification card. */
const COMPANY = [
  { key: 'company_legal_name', prop: 'companyLegalName', labelKey: 'settings.companyName' },
  { key: 'company_vat', prop: 'companyVat', labelKey: 'settings.companyVat' },
  { key: 'company_address', prop: 'companyAddress', labelKey: 'settings.companyAddress' },
  { key: 'company_employer', prop: 'companyEmployer', labelKey: 'settings.companyEmployer' },
] as const;

function CompanyDetails() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['settings'], queryFn: api.settings });
  const readOnly = !usePermissions().can('settings', 'write');
  const [values, setValues] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const s = settings.data?.settings;
    if (s) setValues(Object.fromEntries(COMPANY.map((f) => [f.key, s[f.prop] ?? ''])));
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () => api.updateSettings(Object.fromEntries(COMPANY.map((f) => [f.key, (values[f.key] ?? '').trim()]))),
    onSuccess: async () => {
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      setTimeout(() => setSaved(false), 4000);
    },
  });

  if (!settings.data) return null;

  return (
    <fieldset disabled={readOnly}>
      <Card>
        <h2 className="font-bold">{t('settings.companyTitle')}</h2>
        <p className="text-xs text-muted mb-4">{t('settings.companyHint')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {COMPANY.map((f) => (
            <Field key={f.key} label={t(f.labelKey)}>
              <input
                className="input"
                maxLength={200}
                value={values[f.key] ?? ''}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
              />
            </Field>
          ))}
        </div>
        {save.error ? (
          <p className="text-sm text-danger mt-4">
            {save.error instanceof ApiError ? save.error.message : t('common.saveError')}
          </p>
        ) : null}
        {saved ? <p className="text-sm text-success font-semibold mt-4">{t('settings.saved')}</p> : null}
        <button type="button" className="btn-primary mt-4" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? t('common.saving') : t('settings.saveSettings')}
        </button>
      </Card>
    </fieldset>
  );
}

function ClockInRules() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['settings'], queryFn: api.settings });

  const [values, setValues] = useState<Record<string, string>>({});
  /** One entry per switch, keyed by the setting name the API expects. */
  const [toggles, setToggles] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState(false);
  const readOnly = !usePermissions().can('settings', 'write');

  useEffect(() => {
    const s = settings.data?.settings;
    if (!s) return;
    setValues(Object.fromEntries(FIELDS.map((f) => [f.key, String(s[f.prop])])));
    setToggles({
      allow_clock_in_outside_geofence: s.allowClockInOutsideGeofence,
      allow_clock_in_before_start: s.allowClockInBeforeStart,
      allow_clock_out_after_end: s.allowClockOutAfterEnd,
    });
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      api.updateSettings({
        ...Object.fromEntries(FIELDS.map((f) => [f.key, Number(values[f.key])])),
        ...toggles,
      }),
    onSuccess: async () => {
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      setTimeout(() => setSaved(false), 4000);
    },
  });

  if (settings.isLoading) return <Spinner />;
  if (settings.error) return <ErrorNotice error={settings.error} />;

  return (
    // A view-only permission sees the rules without being offered a save button that
    // the API would refuse.
    <fieldset disabled={readOnly} className="max-w-3xl">
    <Card>
        <h2 className="font-bold mb-4">{t('settings.clockIn')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <Field key={f.key} label={t(f.labelKey)} hint={t(f.hintKey)}>
              <input
                className="input tabular"
                type="number"
                value={values[f.key] ?? ''}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
              />
            </Field>
          ))}
        </div>

        <div className="mt-5 space-y-2">
          {TOGGLES.map((toggle) => (
            <label
              key={toggle.key}
              className="flex items-start gap-3 p-3 rounded bg-surface-alt"
            >
              <input
                type="checkbox"
                className="mt-0.5"
                checked={toggles[toggle.key] ?? true}
                onChange={(e) =>
                  setToggles({ ...toggles, [toggle.key]: e.target.checked })
                }
              />
              <span className="text-sm">
                <strong>{t(toggle.labelKey)}</strong>
                <span className="block text-xs text-muted mt-1 leading-relaxed">
                  {t(toggle.hintKey)}
                </span>
              </span>
            </label>
          ))}
        </div>

        {save.error ? (
          <p className="text-sm text-danger mt-4">
            {save.error instanceof ApiError ? save.error.message : t('common.saveError')}
          </p>
        ) : null}
        {saved ? (
          <p className="text-sm text-success font-semibold mt-4">{t('settings.saved')}</p>
        ) : null}

        <button
          type="button"
          className="btn-primary mt-4"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? t('common.saving') : t('settings.saveSettings')}
        </button>

        {/*
          Two footnotes, where the settings they qualify are.
          The first used to be an amber warning card of its own explaining at length
          that continuous tracking cannot be switched on — answering a question
          nobody asked, in the loudest colour on the page. The second matters more
          than it looks: without it somebody with one enormous site will keep
          widening these global numbers until they are meaningless everywhere.
        */}
        <div className="mt-5 pt-4 border-t border-border space-y-2">
          {/* Where somebody looks for it. The nav link lives in an admin-only group,
              which means a manager sees no trace of the page at all — fine for the
              link, not fine for knowing it exists. */}
          <p className="text-xs text-muted leading-relaxed">
            {t('settings.auditNote')}{' '}
            <Link to="/audit" className="text-primary font-semibold hover:underline">
              {t('nav.audit')}
            </Link>
          </p>
          <p className="text-xs text-muted leading-relaxed">{t('settings.locationNote')}</p>
          <p className="text-xs text-muted leading-relaxed">{t('settings.perJobNote')}</p>
          <p className="text-xs text-muted leading-relaxed">
            {t('settings.perSiteNote')}{' '}
            <Link to="/sites" className="text-primary font-semibold hover:underline">
              {t('settings.openSites')}
            </Link>
          </p>
        </div>
      </Card>
    </fieldset>
  );
}
